import type { AxiosRequestConfig } from "axios";
import type { ResponseDataList } from "@/api/model/baseModel";

import request from "@/plugins/axios";

import DatasetEnum from "@/api/dataset/enums";
import { TradedParams } from "../userCenter";

const isDev =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";
const url = !isDev ? "" : "/api";

/**
 * 查询用户已发布的数据
 */
export function getMyData(options: {
  pageNo: number;
  pageSize: number;
  name?: string;
}) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.MY_DATA_LIST}`,
    method: "post",
    data: options,
  };
  return request<
    ResponseDataList<
      {
        id: number;
        /**
         * 是否有评估报告（ 0：无，1：有）
         */
        isAssessmentReport: 0 | 1;
        datasetName: string;
        price: string;
        datasetCode: string;
        createTime: number;
      }[]
    >
  >(config);
}

/**
 * 查询用户已发布的数据详情
 */
export function getMyDataDetails(id: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.MY_DATA_DETAIL}`,
    method: "post",
    data: { datasetId: id },
  };
  return request<{
    id: number;
    datasetName: string;
    price: number;
    tag: string;
    pattern: string;
    description: string;
    hash: string;
    datasetCode: string;
    datasetSize?: number;
    serverType: number;
    serverUrl: string;
    serverPublicKey: string;
    assessmentMethodIdList: {
      id: number;
      hash: string;
    }[];
    rcrFromSeller: number;
    datasetChargeRatio: number;
  }>(config);
}

/**
 * 发布数据｜修改数据
 */
export function putData(options: {
  datasetName: string;
  price: number;
  pattern: string;
  datasetSize?: number;
  metaJson?: string;
  auditParam?: string;
  id?: number;
  tag?: string;
  description?: string;
  descFormat?: "html" | "markdown";
  descriptionTxt?: string;
  imageList?: {
    imageUrl: string;
    imageOrder: number;
  }[];
  estimateAlgorithmList?: {
    algorithmId: number;
    auditParam: string;
    forwardSig: string;
  }[];
  rcrFromSeller: number;
}) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.PUT_DATA}`,
    method: "post",
    data: options,
  };
  return request<number>(config);
}

// 删除数据
export function deleteData(id: number) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.DELETE_DATA}`,
    method: "post",
    data: {
      datasetId: id,
    },
  };
  return request(config);
}

export type DataMarketItem = {
  id: number;
  isAssessmentReport: 0 | 1;
  datasetName: string;
  price: number;
  datasetCode: string;
  createTime: string;
  tag: string;
  datasetSize: number;
  pattern: string;
  description: string;
  createCompanyName: string;
  salesVolume: number;
  originalPrice: number;
};
export type GetDataMarketsParams = {
  pageSize?: number;
  pageNo?: number;
  orderBy?: string;
  order?: string;
  isCount?: 0 | 1;
  isAll?: 0 | 1;
  name?: string;
  isAssessmentReport?: 0 | 1 | 2;
  formats?: number[];
  recommendId?: number;
};
export type GetDataMarketTagsParams = {
  name?: string;
};
export type PublishDemandParams = {
  description: string;
  id?: number;
};

export type DataMarketsTags = {
  assessmentReport: 0 | 1;
  assessmentReportNot: 0 | 1;
  patternList: {
    code: number;
    name: string;
    count: number;
  }[];
};

export type DataDetail = {
  id: number;
  datasetName: string;
  price: number;
  originalPrice: number;
  tag: string;
  datasetSize: number;
  pattern: string;
  patternFormat: string;
  description: string;
  createCompanyName: string;
  createTime: number;
  salesVolume: number;
  datasetCode: string;
  auditType: number;
  firmName: string;
  bankName: string;
  bankAccount: string;
  isBuy: 0 | 1 | 2;
  assessmentMethodDTOList: {
    templateHtml: string;
    assessmentMethod: string;
    reportId: string;
  }[];
};
/**
 * 查询数据市场数据
 */
export function getDataMarkets(params: GetDataMarketsParams) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.DATA_MARKETS}`,
    method: "post",
    data: params,
  };
  return request<DataMarketItem[]>(config);
}

/**
 * 查询数据市场数据左侧边栏标签
 */
export function getDataMarketsTags(params: GetDataMarketTagsParams) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.DATA_MARKETS_TAGS}`,
    method: "post",
    data: params,
  };
  return request<DataMarketsTags>(config);
}

/**
 * 发布需求
 */
export function publishDemand(params: PublishDemandParams) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.PUBLISH_DEMAND}`,
    method: "post",
    data: params,
  };
  return request(config);
}

/**
 * 查询数据市场数据详情
 */
export function getDataDetail(params: { datasetId: number; deleted?: 0 | 1 }) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.DATA_DETAIL}`,
    method: "post",
    data: {
      datasetId: params.datasetId,
      deleted: params.deleted || 0,
    },
  };
  return request<DataDetail>(config);
}
/**
 * 进行详情保存浏览状态
 */
export function setDataView(data: {
  datasetId: number;
  datasetName: string;
  userId: number;
  type: number;
}) {
  const config: AxiosRequestConfig = {
    url: `${url}/trend/view/save`,
    method: "post",
    data,
  };
  return request(config);
}
/**
 * 不再提示发布需求
 */
export function closeRemind() {
  const config: AxiosRequestConfig = {
    url: `${url}/user/closeRemind`,
    method: "post",
  };
  return request(config);
}
/**
 * 弹出提示状态
 */
export function setPopupFlag() {
  const config: AxiosRequestConfig = {
    url: `${url}/user/setPopupFlag`,
    method: "post",
  };
  return request(config);
}
/**
 * 是否允许弹窗提示
 * type:0:浏览 1:其他
 */
export function checkPopup(type: 0 | 1) {
  console.log("type:", type);
  const config: AxiosRequestConfig = {
    url: `${url}/user/checkPopup`,
    method: "get",
    params: { type },
  };
  return request(config);
}
/**
 * 数据质量验证报告申请
 */
export type ApplyQualityReportParams = {
  datasetId: number;
  assessmentMethodList: {
    assessmentMethodId: number;
    assessmentMethod?: string;
  }[];
  description?: string;
};
export function applyQualityReport(params: ApplyQualityReportParams) {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.APPLY_QUALITY_REPORT}`,
    method: "post",
    data: params,
  };
  return request(config);
}

// 获取托管算法（典公钥）列表
export function getAlgorithmList() {
  const config: AxiosRequestConfig = {
    url: `${url}${DatasetEnum.GET_ALGORITHM_LIST}`,
    method: "post",
  };
  return request<
    {
      id: number;
      publicKey: string;
    }[]
  >(config);
}

// 2. 第三方服务器连接测试接口
export interface ServiceInfo {
  serviceName: string;
  serviceVersion: string;
  serviceStatus: number;
}

export interface VerifyServerResponse {
  resultCode: number;
  resultDesc: string;
  data: ServiceInfo[];
  dataList: null;
  page: null;
}
export function verifyServer(serverUrl: string) {
  const config: AxiosRequestConfig = {
    url: `${serverUrl}:6777/connector/checkServiceStatus`,
    method: "post",
    timeout: 60000,
  };
  return request<ServiceInfo[]>(config);
}
export function getDianPublicKey(serverUrl: string) {
  const config: AxiosRequestConfig = {
    url: `${serverUrl}:6888/fidelius/getDianPublicKey`,
    method: "post",
  };
  return request<{ DianPublicKey: string }>(config);
}

// 3. 保存数据集接口
export type SaveDatasetParams = {
  datasetName: string;
  price: number;
  pattern: string;
  datasetSize: number; // 压缩后的大小
  datasetActualSize?: number; // 实际大小（解压后）
  metaJson: string;
  auditParam: string;
  id?: number;
  tag?: string;
  description?: string;
  descriptionTxt?: string;
  estimateAlgorithmList?: {
    algorithmId: number;
    auditParam: string;
    forwardSig: string;
  }[];
  auditType: 0 | 1;
  serverType: 0 | 1;
  serverUrl: string;
  serverPublicKey: string;
  serverPrivateKey: string;
  rcrFromSeller: number;
};

export function saveDataset(params: SaveDatasetParams) {
  const config: AxiosRequestConfig = {
    url: `${url}/dataset/new/save`,
    method: "post",
    data: params,
  };
  return request(config);
}
/**
 * 我的订单-企业交付
 */
export function getFirmOrderList(params: TradedParams) {
  const config: AxiosRequestConfig = {
    url: `${url}/firm/order/buyerOrder`,
    method: "post",
    data: params,
    headers: {
      "Content-Type": "application/json",
    },
  };
  return request(config);
}
